import React, { useCallback, useState } from 'react';

import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { ExpandMore } from '@mui/icons-material';

import { Button } from '../../../Button';
import {
  BaseDashboardActionItem, BaseDashboardActionItemProps
} from './BaseDashboardActionItem.component';
import { ExpandableSection } from './ExpandableSection.component';

export type ExpandableDashboardActionItemProps = BaseDashboardActionItemProps & {
  children: React.ReactNode;
  expandText?: React.ReactNode;
  grayBackground?: boolean;
  hideText?: React.ReactNode;
  isExpanded?: boolean;
  onExpandedChange?: (isExpanded: boolean) => void;
  testId?: string;
};

export const ExpandableDashboardActionItem: React.FC<ExpandableDashboardActionItemProps> = ({
  isExpanded,
  onExpandedChange,
  expandText,
  hideText,
  children,
  grayBackground = true,
  testId = 'expandable-dash-action-item',
  ...props
}) => {
  const { classes, cx } = useExpandStyles();
  const [ internalExpanded, setExpanded ] = useState(false);

  const handleExpandChange = useCallback(() => {
    if (isExpanded && onExpandedChange) {
      onExpandedChange(!isExpanded);
    } else {
      setExpanded(!internalExpanded);
    }
  }, [ internalExpanded, isExpanded, onExpandedChange ]);

  const expanded = isExpanded ?? internalExpanded;

  return (
    <BaseDashboardActionItem
      {...props}
      testId={testId}
      expandTextContainer={(
        <Button
          className={classes.button}
          disableFocusRipple
          disableRipple
          onClick={handleExpandChange}
          data-testid="expand-action-item-btn"
          endIcon={(
            <ExpandMore
              className={cx({
                [classes.arrowIcon]: true,
                [classes.arrowIconRotated]: expanded,
              })}
            />
          )}
        >
          <Typography variant="body1">
            {expanded ? hideText ?? expandText : expandText}
          </Typography>
        </Button>
      )}
    >
      <ExpandableSection isExpanded={expanded} testId="dash-action-item-expandable-section">
        <Box className={cx(grayBackground && classes.grayContainer)}>
          <div>
            {children}
          </div>
        </Box>
      </ExpandableSection>
    </BaseDashboardActionItem>
  );
};

const useExpandStyles = makeStyles()(theme => ({
  arrowIcon: {
    color: theme.palette.text.secondary,
    fontSize: '34px',
    transform: 'rotate(0deg)',
    transition: 'transform 300ms',
  },
  arrowIconRotated: {
    transform: 'rotate(180deg)',
  },
  button: {
    padding: theme.spacing(1, 0, 1),
    textTransform: 'none',
    '&:hover': {
      backgroundColor: 'unset'
    }
  },
  container: {
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
  },
  grayContainer: {
    background: theme.palette.grey[100],
    padding: theme.spacing(2),
    width: '100%',
    '& > div': {
      backgroundColor: theme.palette.common.white,
    }
  }
}));
